import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Spinner,
    Modal,
    Form,
    Alert,
} from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import socket from '../../utilities/socket';
import { attachVendorOrderRooms } from '../../hooks/useVendorOrderRooms';
import { vendorOwnerIdOf } from '../../utilities/vendorScope';
import RiderCredentialsModal from '../../components/RiderCredentialsModal';
import {
    MdAccountBalanceWallet,
    MdBlock,
    MdBolt,
    MdCheckCircle,
    MdDirectionsBike,
    MdPayments,
    MdStar,
    MdStarBorder,
    MdWifi,
    MdWifiOff,
} from 'react-icons/md';
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL || '';

const colors = {
    white: '#ffffff',
    gray: { 50: '#f9fafb', 100: '#f3f4f6', 300: '#d1d5db', 400: '#9ca3af', 600: '#4b5563', 700: '#374151' },
    green: '#006202ff',
    blue: '#023485ff',
    red: '#dc2626',
    yellow: '#f59e0b',
};

// Vendor type → vendor-specific rider route segment
const vendorRiderRoute = {
    RESTAURANT: 'restaurant-vendor',
    RETAILER: 'retailer-wholesaler-vendor',
    WHOLESALER: 'retailer-wholesaler-vendor',
    HEALTHCARE: 'healthcare-vendor',
    BRAND_OWNER: 'brand-owner',
};

const VendorRiders = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    // The owner's id (4 Oct 2026): a team member manages the business's
    // riders, and the server lists them under the owner.
    const vendorId = vendorOwnerIdOf(user);
    const token = user?.token;
    // operationMode is the canonical field on the user object (RESTAURANT / RETAILER / WHOLESALER / HEALTHCARE)
    const vendorType = (user?.result?.operationMode || 'RESTAURANT').toUpperCase();
    const routeSegment = vendorRiderRoute[vendorType] || 'restaurant-vendor';

    const [riders, setRiders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [createdCredentials, setCreatedCredentials] = useState(null);
    const [editingRider, setEditingRider] = useState(null);
    const [saving, setSaving] = useState(false);
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);
    const [filter, setFilter] = useState('all');
    const [showRiderId, setShowRiderId] = useState(false);
    const [hoveredRiderId, setHoveredRiderId] = useState(null);
    const [, setTick] = useState(0);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        mobile: '',
        email: '',
        nationalId: '',
        vehicleType: 'motorcycle',
        vehiclePlateNumber: '',
        password: '',
    });
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (vendorId && token) {
            fetchRiders();
        } else {
            setLoading(false);
            setError('Vendor ID not found. Please log in again.');
        }
    }, [vendorId, token, routeSegment]);

    // Tick every 60s so "Xm ago" labels update live
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 60000);
        return () => clearInterval(interval);
    }, []);

    // Socket: live rider status and heartbeats. One join names the owner and
    // the server picks the rooms (4 Oct 2026): the whole business for head
    // office, the member's branch rooms for a member limited to some
    // branches. The rooms stay joined when this screen closes, because the
    // navbar and the order screens share this socket and listen in them too.
    useEffect(() => {
        if (!vendorId) return;

        // The connect handler inside attachVendorOrderRooms rejoins after a
        // reconnect; a server older than this page gets the old join.
        const stopRooms = attachVendorOrderRooms(socket, vendorId, {
            fallback: (s) => s.emit('joinVendorRoom', vendorId),
        });

        const handleRiderStatusChange = ({ riderId, status, isAvailable }) => {
            setRiders(prev =>
                prev.map(r =>
                    r._id === riderId
                        ? { ...r, onlineStatus: status, availability: { ...r.availability, isAvailable } }
                        : r
                )
            );
        };

        const handleGlobalRiderStatusUpdate = ({ riderId, status }) => {
            setRiders(prev =>
                prev.map(r =>
                    r._id === riderId
                        ? { ...r, onlineStatus: status, availability: { ...r.availability, isAvailable: status === 'Online' } }
                        : r
                )
            );
        };

        const handleHeartbeatUpdate = ({ riderId, lastActiveAt }) => {
            setRiders(prev =>
                prev.map(r => r._id === riderId ? { ...r, lastActiveAt } : r)
            );
        };

        let isMounted = true;
        const handleReconnect = () => {
            if (isMounted) fetchRiders();
        };

        socket.on('rider:statusChanged', handleRiderStatusChange);
        socket.on('riderStatusUpdate', handleGlobalRiderStatusUpdate);
        socket.on('rider:heartbeatUpdate', handleHeartbeatUpdate);
        socket.on('connect', handleReconnect);

        return () => {
            isMounted = false;
            socket.off('rider:statusChanged', handleRiderStatusChange);
            socket.off('riderStatusUpdate', handleGlobalRiderStatusUpdate);
            socket.off('rider:heartbeatUpdate', handleHeartbeatUpdate);
            socket.off('connect', handleReconnect);
            stopRooms();
        };
    }, [vendorId]);

    const fetchRiders = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_URL}/riders/${routeSegment}/${vendorId}/riders`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const riderList = response.data?.data?.riders || response.data || [];
            setRiders(Array.isArray(riderList) ? riderList : []);
        } catch (err) {
            console.error('Error fetching vendor riders:', err);
            setError('Failed to load riders');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (rider = null) => {
        if (rider) {
            setEditingRider(rider);
            setFormData({
                firstName: rider.firstName || '',
                lastName: rider.lastName || '',
                mobile: rider.mobile || rider.phoneNumber || '',
                email: rider.email || '',
                nationalId: rider.nationalId || '',
                vehicleType: (rider.carrierType || rider.vehicleDetails?.vehicleType || 'Motorcycle').toLowerCase(),
                vehiclePlateNumber: rider.vehicleDetails?.plateNumber || '',
                password: '',
            });
        } else {
            setEditingRider(null);
            setFormData({
                firstName: '',
                lastName: '',
                mobile: '',
                email: '',
                nationalId: '',
                vehicleType: 'motorcycle',
                vehiclePlateNumber: '',
                password: '',
            });
        }
        setFieldErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRider(null);
        setFieldErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error for this field as the user types
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'First name is required.';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required.';
        if (!formData.mobile.trim()) {
            errors.mobile = 'Phone number is required.';
        } else if (!/^\+?[0-9\s\-]{7,15}$/.test(formData.mobile.trim())) {
            errors.mobile = 'Enter a valid phone number (e.g. +256701234567).';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            errors.email = 'Enter a valid email address.';
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});
        setSaving(true);
        try {
            const riderData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                mobile: formData.mobile,
                email: formData.email,
                nationalId: formData.nationalId,
                carrierType: formData.vehicleType,
                vehicleDetails: {
                    vehicleType: formData.vehicleType,
                    plateNumber: formData.vehiclePlateNumber,
                },
            };

            if (editingRider) {
                await axios.put(
                    `${API_URL}/riders/${routeSegment}/${vendorId}/riders/${editingRider._id}`,
                    riderData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Rider updated successfully');
                handleCloseModal();
            } else {
                const response = await axios.post(
                    `${API_URL}/riders/${routeSegment}/${vendorId}/riders`,
                    { ...riderData, password: formData.password || undefined },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const result = response.data?.result || response.data;
                if (result?.tempPassword) {
                    setCreatedCredentials({
                        name: `${formData.firstName} ${formData.lastName}`,
                        phone: formData.mobile,
                        email: formData.email,
                        tempPassword: result.tempPassword,
                    });
                    setShowCredentialsModal(true);
                }
                toast.success('Rider created successfully');
                handleCloseModal();
            }
            fetchRiders();
        } catch (err) {
            console.error('Error saving rider:', err);
            toast.error(
                err.response?.data?.error?.message ||
                err.response?.data?.message ||
                'Failed to save rider'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (rider) => {
        setStatusUpdatingId(rider._id);
        try {
            const newStatus = rider.activeStatus === 'Active' ? 'Inactive' : 'Active';
            await axios.put(
                `${API_URL}/riders/${routeSegment}/${vendorId}/riders/${rider._id}`,
                { activeStatus: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Rider ${newStatus === 'Active' ? 'activated' : 'deactivated'}`);
            fetchRiders();
        } catch (err) {
            toast.error(
                err.response?.data?.error?.message ||
                err.response?.data?.message ||
                'Failed to update rider status'
            );
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const filteredRiders = riders.filter((rider) => {
        if (filter === 'all') return true;
        if (filter === 'active') return rider.activeStatus === 'Active';
        if (filter === 'inactive') return rider.activeStatus !== 'Active';
        if (filter === 'online') return rider.onlineStatus === 'Online';
        return true;
    });

    const riderMetrics = {
        total: riders.length,
        ready: riders.filter((r) => r.activeStatus === 'Active' && r.onlineStatus === 'Online').length,
        active: riders.filter((r) => r.activeStatus === 'Active').length,
        inactive: riders.filter((r) => r.activeStatus !== 'Active').length,
        online: riders.filter((r) => r.onlineStatus === 'Online').length,
        offline: riders.filter((r) => r.onlineStatus !== 'Online').length,
    };

    const fleetEarnings = {
        totalEarned: riders.reduce((sum, rider) => sum + (rider.earnings?.totalEarned || 0), 0),
        pendingPayout: riders.reduce((sum, rider) => sum + (rider.earnings?.pendingPayout || 0), 0),
    };

    const getVehicleColor = (vehicleType) => {
        const t = (vehicleType || '').toLowerCase();
        return { bicycle: '#f5a30bff', motorcycle: '#3b82f6', car: '#8b5cf6', van: '#10b981', truck: '#ef4444' }[t] || colors.blue;
    };

    const getVehicleIcon = (vehicleType) => {
        const t = (vehicleType || '').toLowerCase();
        return { bicycle: '🚲', motorcycle: '🏍️', car: '🚗', van: '🚐', truck: '🚚' }[t] || '🏍️';
    };

    const getRiderInitials = (rider) => {
        const first = rider.firstName?.[0] || '';
        const last = rider.lastName?.[0] || '';
        return `${first}${last}`.toUpperCase() || 'RD';
    };

    const getVehicleLabel = (rider) =>
        rider.carrierType || rider.vehicleDetails?.vehicleType || 'Motorcycle';

    const getLastSeenLabel = (rider) => {
        if (!rider.lastActiveAt) return 'No heartbeat yet';
        const minutesAgo = Math.floor((Date.now() - new Date(rider.lastActiveAt).getTime()) / 60000);
        if (minutesAgo <= 0) return 'Active just now';
        if (minutesAgo < 60) return `Active ${minutesAgo}m ago`;
        const hoursAgo = Math.floor(minutesAgo / 60);
        if (hoursAgo < 24) return `Active ${hoursAgo}h ago`;
        const daysAgo = Math.floor(hoursAgo / 24);
        return `Active ${daysAgo}d ago`;
    };

    const getAccountStatusConfig = (rider) =>
        rider.activeStatus === 'Active'
            ? {
                label: 'Active account',
                tone: '#166534',
                background: '#dcfce7',
                border: '#86efac',
                helper: 'Can receive assignments',
            }
            : {
                label: 'Inactive account',
                tone: '#991b1b',
                background: '#fee2e2',
                border: '#fecaca',
                helper: 'Blocked from assignments',
            };

    const getPresenceStatusConfig = (rider) => {
        switch (rider.onlineStatus) {
            case 'Online':
                return {
                    label: 'Online',
                    tone: '#166534',
                    background: '#dcfce7',
                    border: '#86efac',
                    helper: rider.activeStatus === 'Active' ? 'Live and reachable' : 'Live but account disabled',
                };
            case 'Busy':
                return {
                    label: 'Busy',
                    tone: '#92400e',
                    background: '#fef3c7',
                    border: '#fcd34d',
                    helper: 'Currently handling an order',
                };
            case 'inTransit':
                return {
                    label: 'In transit',
                    tone: '#1d4ed8',
                    background: '#dbeafe',
                    border: '#93c5fd',
                    helper: 'Currently on a delivery route',
                };
            default:
                return {
                    label: 'Offline',
                    tone: '#6b7280',
                    background: '#f3f4f6',
                    border: '#d1d5db',
                    helper: getLastSeenLabel(rider),
                };
        }
    };

    const renderStatusPill = (config) => (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '999px',
                padding: '6px 10px',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: config.tone,
                backgroundColor: config.background,
                border: `1px solid ${config.border}`,
                whiteSpace: 'nowrap',
            }}
        >
            {config.label}
        </span>
    );

    const renderStatusCell = (rider) => {
        const accountConfig = getAccountStatusConfig(rider);
        const presenceConfig = getPresenceStatusConfig(rider);
        return (
            <div style={{ minWidth: 180 }}>
                <div className="d-flex flex-wrap gap-2 mb-2">
                    {renderStatusPill(accountConfig)}
                    {renderStatusPill(presenceConfig)}
                </div>
                <small style={{ color: colors.gray[600], display: 'block', fontSize: '0.75rem', lineHeight: 1.35 }}>{accountConfig.helper}</small>
                <small style={{ color: colors.gray[600], display: 'block', fontSize: '0.75rem', lineHeight: 1.35 }}>{presenceConfig.helper}</small>
            </div>
        );
    };

    const getRatingSummary = (rider) => {
        const totalRatings = rider.performanceMetrics?.totalRatings || 0;
        if (totalRatings <= 0) {
            return {
                average: null,
                label: 'Not rated yet',
                helper: 'Waiting for first customer review',
            };
        }
        const average = Number(rider.performanceMetrics?.averageRating || 0);
        return {
            average,
            label: `${average.toFixed(1)} / 5`,
            helper: `${totalRatings} rating${totalRatings === 1 ? '' : 's'}`,
        };
    };

    const renderRatingStars = (average) => {
        const rounded = Math.round(average);
        return Array.from({ length: 5 }, (_, index) =>
            index < rounded ? (
                <MdStar key={`filled-${index}`} style={{ color: '#f59e0b', fontSize: '1rem' }} />
            ) : (
                <MdStarBorder key={`empty-${index}`} style={{ color: '#d1d5db', fontSize: '1rem' }} />
            )
        );
    };

    const formatCurrency = (amount) => `UGX ${Number(amount || 0).toLocaleString()}`;

    const formatDateLabel = (value) => {
        if (!value) return 'No payout yet';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'No payout yet';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getEarningsSummary = (rider) => {
        const totalEarned = rider.earnings?.totalEarned || 0;
        const pendingPayout = rider.earnings?.pendingPayout || 0;
        const todayEarnings = rider.earnings?.todayEarnings || 0;
        const lastPayoutAmount = rider.earnings?.lastPayoutAmount || 0;
        const lastPayoutDate = rider.earnings?.lastPayoutDate || null;

        if (totalEarned <= 0) {
            return {
                headline: 'No earnings yet',
                helper: 'Will populate after first completed delivery',
                detail: 'Pending payout: UGX 0',
                tone: '#475569',
                accent: '#e2e8f0',
            };
        }

        if (pendingPayout > 0) {
            return {
                headline: formatCurrency(totalEarned),
                helper: `Pending payout: ${formatCurrency(pendingPayout)}`,
                detail: todayEarnings > 0
                    ? `Today: ${formatCurrency(todayEarnings)}`
                    : 'Awaiting wallet settlement',
                tone: '#9a3412',
                accent: '#fdba74',
            };
        }

        return {
            headline: formatCurrency(totalEarned),
            helper: lastPayoutAmount > 0
                ? `Last payout: ${formatCurrency(lastPayoutAmount)}`
                : 'No pending payout right now',
            detail: `Settled on ${formatDateLabel(lastPayoutDate)}`,
            tone: '#166534',
            accent: '#86efac',
        };
    };

    const getRowSurfaceColor = (rider, isHovered) => {
        if (!isHovered) return '#ffffff';
        if (rider.activeStatus !== 'Active') return '#f4fdff';
        if (rider.onlineStatus === 'Online') return '#f0fdf4';
        return '#f8fafc';
    };

    const getCellStyle = (rider, isHovered, edge = 'middle', extra = {}) => {
        const backgroundColor = getRowSurfaceColor(rider, isHovered);
        const style = {
            backgroundColor,
            borderTop: '1px solid rgba(226, 232, 240, 0.95)',
            borderBottom: '1px solid rgba(226, 232, 240, 0.95)',
            verticalAlign: 'middle',
            transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
            ...extra,
        };

        if (edge === 'left') {
            style.borderLeft = '1px solid rgba(226, 232, 240, 0.95)';
            style.borderTopLeftRadius = 18;
            style.borderBottomLeftRadius = 18;
        }

        if (edge === 'right') {
            style.borderRight = '1px solid rgba(226, 232, 240, 0.95)';
            style.borderTopRightRadius = 18;
            style.borderBottomRightRadius = 18;
        }

        return style;
    };

    const getStatusActionButtonStyle = (rider) => {
        const isActive = rider.activeStatus === 'Active';
        return {
            backgroundColor: isActive ? '#b42318' : '#15803d',
            borderColor: isActive ? '#b42318' : '#15803d',
            color: '#ffffff',
            borderRadius: 12,
            minWidth: 118,
            fontWeight: 700,
            boxShadow: isActive
                ? '0 10px 18px rgba(180, 35, 24, 0.18)'
                : '0 10px 18px rgba(21, 128, 61, 0.18)',
        };
    };

    const getActionCellInnerStyle = () => ({
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
    });

    const summaryCards = [
        {
            key: 'total',
            label: 'Total Riders',
            value: riderMetrics.total,
            helper: 'All registered in-house riders',
            icon: <MdDirectionsBike size={34} />,
            accent: colors.blue,
            background: 'rgba(2, 52, 133, 0.08)',
        },
        {
            key: 'ready',
            label: 'Ready Now',
            value: riderMetrics.ready,
            helper: 'Active and online for dispatch',
            icon: <MdBolt size={34} />,
            accent: colors.green,
            background: 'rgba(0, 98, 2, 0.08)',
        },
        {
            key: 'active',
            label: 'Active Accounts',
            value: riderMetrics.active,
            helper: 'Enabled to receive assignments',
            icon: <MdCheckCircle size={34} />,
            accent: '#ca8a04',
            background: 'rgba(245, 158, 11, 0.12)',
        },
        {
            key: 'inactive',
            label: 'Inactive Accounts',
            value: riderMetrics.inactive,
            helper: 'Disabled until reactivated',
            icon: <MdBlock size={34} />,
            accent: colors.red,
            background: 'rgba(220, 38, 38, 0.08)',
        },
    ];

    if (loading) {
        return (
            <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="grow" style={{ color: 'rgba(127, 40, 19, 0.84)' }} />
            </div>
        );
    }

    return (
        <div className="content" style={{ marginTop: '-50px' }}>
            <Row className="mb-4">
                <Col>
                    <h4 style={{ color: colors.gray[700] }}>
                        <i className="nc-icon nc-delivery-fast me-2" />
                        My Riders
                    </h4>
                    <p style={{ color: colors.gray[600] }}>Manage your in-house delivery fleet</p>
                    <div style={{ display: 'flex', gap: '18px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                            <MdWifi style={{ color: '#16a34a', fontSize: '18px' }} /> {riderMetrics.online} Online
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                            <MdWifiOff style={{ color: '#dc2626', fontSize: '18px' }} /> {riderMetrics.offline} Offline
                        </span>
                    </div>
                </Col>
                <Col className="text-end">
                    <Button
                        style={{ backgroundColor: colors.blue, borderColor: colors.blue }}
                        onClick={() => handleOpenModal()}
                    >
                        <i className="nc-icon nc-simple-add me-2" />
                        Add Rider
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Stats Cards */}
            <Row className="mb-4">
                {summaryCards.map((card) => (
                    <Col md="3" sm="6" key={card.key}>
                        <Card
                            className="card-stats h-100"
                            style={{
                                border: `1px solid ${card.background}`,
                                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
                            }}
                        >
                            <CardBody>
                                <div className="d-flex align-items-start justify-content-between">
                                    <div
                                        style={{
                                            width: 58,
                                            height: 58,
                                            borderRadius: 18,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: card.accent,
                                            backgroundColor: card.background,
                                        }}
                                    >
                                        {card.icon}
                                    </div>
                                    <div className="text-end">
                                        <p className="card-category mb-1">{card.label}</p>
                                        <CardTitle tag="h3" style={{ margin: 0, color: colors.gray[700] }}>
                                            {card.value}
                                        </CardTitle>
                                    </div>
                                </div>
                                <div style={{ marginTop: 14, color: colors.gray[600], fontSize: '0.85rem' }}>
                                    {card.helper}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="mb-3">
                <Col md="6">
                    <Card
                        className="h-100"
                        style={{
                            border: '1px solid rgba(14, 116, 144, 0.12)',
                            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <CardBody>
                            <div className="d-flex align-items-start justify-content-between">
                                <div
                                    style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#0f766e',
                                        backgroundColor: 'rgba(20, 184, 166, 0.12)',
                                    }}
                                >
                                    <MdAccountBalanceWallet size={30} />
                                </div>
                                <div className="text-end">
                                    <p className="card-category mb-1">Fleet Earnings</p>
                                    <CardTitle tag="h3" style={{ margin: 0, color: colors.gray[700] }}>
                                        {formatCurrency(fleetEarnings.totalEarned)}
                                    </CardTitle>
                                </div>
                            </div>
                            <div style={{ marginTop: 14, color: colors.gray[600], fontSize: '0.85rem' }}>
                                Lifetime earnings recorded across your riders
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md="6">
                    <Card
                        className="h-100"
                        style={{
                            border: '1px solid rgba(217, 119, 6, 0.14)',
                            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
                        }}
                    >
                        <CardBody>
                            <div className="d-flex align-items-start justify-content-between">
                                <div
                                    style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#b45309',
                                        backgroundColor: 'rgba(245, 158, 11, 0.16)',
                                    }}
                                >
                                    <MdPayments size={30} />
                                </div>
                                <div className="text-end">
                                    <p className="card-category mb-1">Awaiting Payout</p>
                                    <CardTitle tag="h3" style={{ margin: 0, color: colors.gray[700] }}>
                                        {formatCurrency(fleetEarnings.pendingPayout)}
                                    </CardTitle>
                                </div>
                            </div>
                            <div style={{ marginTop: 14, color: colors.gray[600], fontSize: '0.85rem' }}>
                                Pending rider earnings not yet settled
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Filter Buttons */}
            <Row className="mb-3">
                <Col>
                    <Button variant={filter === 'all' ? 'primary' : 'outline-primary'} size="sm" className="me-2 mb-2" onClick={() => setFilter('all')}>
                        All ({riderMetrics.total})
                    </Button>
                    <Button variant={filter === 'online' ? 'success' : 'outline-success'} size="sm" className="me-2 mb-2" onClick={() => setFilter('online')}>
                        Online ({riderMetrics.online})
                    </Button>
                    <Button variant={filter === 'active' ? 'warning' : 'outline-warning'} size="sm" className="me-2 mb-2" onClick={() => setFilter('active')}>
                        Active ({riderMetrics.active})
                    </Button>
                    <Button variant={filter === 'inactive' ? 'danger' : 'outline-danger'} size="sm" className="mb-2" onClick={() => setFilter('inactive')}>
                        Inactive ({riderMetrics.inactive})
                    </Button>
                </Col>
            </Row>

            {/* Riders Table */}
            <Card>
                <CardHeader>
                    <CardTitle tag="h5" style={{ color: colors.gray[700] }}>
                        Rider List ({filteredRiders.length})
                    </CardTitle>
                </CardHeader>
                <CardBody>
                    {filteredRiders.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="nc-icon nc-single-02" style={{ fontSize: '48px', color: colors.gray[300] }} />
                            <p className="mt-3" style={{ color: colors.gray[600] }}>
                                No riders found. Click "Add Rider" to get started.
                            </p>
                        </div>
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                paddingBottom: 6,
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                        <Table
                            responsive={false}
                            style={{
                                borderCollapse: 'separate',
                                borderSpacing: '0 14px',
                                marginBottom: 0,
                                minWidth: 1680,
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Name</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                        Rider ID{' '}
                                        <i
                                            className={`nc-icon ${showRiderId ? 'nc-glasses-2' : 'nc-key-25'}`}
                                            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '0.9rem' }}
                                            onClick={() => setShowRiderId(!showRiderId)}
                                            title={showRiderId ? 'Hide Rider IDs' : 'Show Rider IDs'}
                                        />
                                    </th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Phone</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Vehicle</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Status</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Deliveries</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Earnings</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Rating</th>
                                    <th style={{ border: 0, color: colors.gray[400], fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRiders.map((rider) => {
                                    const isHovered = hoveredRiderId === rider._id;
                                    const earnings = getEarningsSummary(rider);
                                    return (
                                    <tr
                                        key={rider._id}
                                        onMouseEnter={() => setHoveredRiderId(rider._id)}
                                        onMouseLeave={() => setHoveredRiderId(null)}
                                    >
                                        <td
                                            style={getCellStyle(rider, isHovered, 'left', {
                                                verticalAlign: 'middle',
                                                minWidth: 260,
                                                boxShadow: rider.onlineStatus === 'Online'
                                                    ? 'inset 3px 0 0 #22c55e'
                                                    : rider.activeStatus !== 'Active'
                                                        ? 'inset 3px 0 0 #ef4444'
                                                        : 'inset 3px 0 0 #cbd5e1',
                                            })}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    style={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 16,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: rider.activeStatus === 'Active'
                                                            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.16), rgba(59, 130, 246, 0.18))'
                                                            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.14), rgba(148, 163, 184, 0.16))',
                                                        color: rider.activeStatus === 'Active' ? '#166534' : '#991b1b',
                                                        fontWeight: 800,
                                                        letterSpacing: '0.04em',
                                                    }}
                                                >
                                                    {getRiderInitials(rider)}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.gray[700], lineHeight: 1.3 }}>
                                                        {rider.firstName} {rider.lastName}
                                                    </div>
                                                    <div style={{ color: colors.gray[600], fontSize: '0.78rem', lineHeight: 1.3 }}>
                                                        {rider.email || 'No email on file'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            {showRiderId ? (
                                                <code style={{ fontSize: '0.78rem', backgroundColor: colors.gray[100], padding: '8px 10px', borderRadius: '10px', color: colors.gray[700], display: 'inline-block' }}>
                                                    {rider._id}
                                                </code>
                                            ) : (
                                                <span style={{ color: colors.gray[400], fontWeight: 700, letterSpacing: '0.16em' }}>••••••</span>
                                            )}
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            <div
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    padding: '10px 12px',
                                                    borderRadius: 14,
                                                    backgroundColor: 'rgba(15, 23, 42, 0.04)',
                                                    fontWeight: 700,
                                                    color: colors.gray[700],
                                                }}
                                            >
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                    {rider.mobile || rider.phoneNumber}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            <div className="d-flex align-items-center gap-3">
                                                <Badge
                                                    bg=""
                                                    style={{
                                                        backgroundColor: getVehicleColor(getVehicleLabel(rider)),
                                                        color: '#fff',
                                                        padding: '10px 14px',
                                                        fontSize: '1.7rem',
                                                        lineHeight: 1,
                                                        borderRadius: 14,
                                                        boxShadow: '0 10px 18px rgba(15, 23, 42, 0.12)',
                                                    }}
                                                    title={getVehicleLabel(rider)}
                                                >
                                                    {getVehicleIcon(getVehicleLabel(rider))}
                                                </Badge>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: colors.gray[700], lineHeight: 1.3 }}>{getVehicleLabel(rider)}</div>
                                                    <small style={{ color: colors.gray[600], fontSize: '0.78rem' }}>
                                                        {rider.vehicleDetails?.plateNumber || 'No plate number'}
                                                    </small>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            {renderStatusCell(rider)}
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.gray[700], lineHeight: 1.3 }}>
                                                {rider.performanceMetrics?.totalDeliveries || 0}
                                            </div>
                                            <small style={{ color: colors.gray[600], fontSize: '0.78rem' }}>
                                                {rider.activeStatus === 'Active' ? 'Completed trips' : 'Trips stay recorded'}
                                            </small>
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            <div style={{ minWidth: 180 }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.gray[700], lineHeight: 1.3 }}>
                                                    {earnings.headline}
                                                </div>
                                                <small
                                                    style={{
                                                        color: earnings.tone,
                                                        display: 'inline-block',
                                                        marginTop: 6,
                                                        marginBottom: 6,
                                                        padding: '3px 8px',
                                                        borderRadius: 999,
                                                        backgroundColor: `${earnings.accent}22`,
                                                        border: `1px solid ${earnings.accent}`,
                                                        fontWeight: 600,
                                                        fontSize: '0.72rem',
                                                    }}
                                                >
                                                    {earnings.helper}
                                                </small>
                                                <small style={{ color: colors.gray[600], display: 'block', fontSize: '0.75rem' }}>
                                                    {earnings.detail}
                                                </small>
                                            </div>
                                        </td>
                                        <td style={getCellStyle(rider, isHovered)}>
                                            {(() => {
                                                const rating = getRatingSummary(rider);
                                                if (rating.average === null) {
                                                    return (
                                                        <div>
                                                            <div className="d-flex align-items-center gap-1 mb-1" style={{ color: colors.gray[400] }}>
                                                                <MdStarBorder style={{ fontSize: '1rem' }} />
                                                                <span style={{ fontWeight: 600 }}>{rating.label}</span>
                                                            </div>
                                                            <small style={{ color: colors.gray[600] }}>{rating.helper}</small>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div>
                                                        <div className="d-flex align-items-center gap-1 mb-1">
                                                            <span className="d-inline-flex align-items-center">
                                                                {renderRatingStars(rating.average)}
                                                            </span>
                                                            <strong style={{ color: colors.gray[700] }}>{rating.label}</strong>
                                                        </div>
                                                        <small style={{ color: colors.gray[600] }}>{rating.helper}</small>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td
                                            style={getCellStyle(rider, isHovered, 'right', {
                                                verticalAlign: 'middle',
                                                minWidth: 180,
                                                padding: '14px 16px',
                                                overflow: 'hidden',
                                            })}
                                        >
                                            <div style={getActionCellInnerStyle()}>
                                                <Button
                                                    variant=""
                                                    size="sm"
                                                    onClick={() => handleOpenModal(rider)}
                                                    style={{
                                                        backgroundColor: isHovered ? '#dbeafe' : '#eff6ff',
                                                        borderColor: '#93c5fd',
                                                        color: colors.blue,
                                                        borderRadius: 12,
                                                        minWidth: 44,
                                                    }}
                                                >
                                                    <i className="nc-icon nc-settings-gear-64" />
                                                </Button>
                                                <Button
                                                    variant=""
                                                    size="sm"
                                                    onClick={() => handleToggleStatus(rider)}
                                                    style={getStatusActionButtonStyle(rider)}
                                                    disabled={statusUpdatingId === rider._id}
                                                >
                                                    {statusUpdatingId === rider._id ? (
                                                        <><Spinner size="sm" animation="border" className="me-2" />Updating...</>
                                                    ) : (
                                                        rider.activeStatus === 'Active' ? 'Deactivate' : 'Activate'
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </Table>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Add/Edit Rider Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: colors.blue, color: colors.white }}>
                    <Modal.Title>{editingRider ? 'Edit Rider' : 'Add New Rider'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit} noValidate>
                    <Modal.Body>
                        {/* Required fields legend */}
                        <p style={{ fontSize: '0.78rem', color: colors.gray[400], marginBottom: '18px', marginTop: '-4px' }}>
                            Fields marked <span style={{ color: colors.red, fontWeight: 700 }}>*</span> are required.
                        </p>

                        <Row>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        First Name <span style={{ color: colors.red }}>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        placeholder="e.g. John"
                                        isInvalid={!!fieldErrors.firstName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.firstName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Last Name <span style={{ color: colors.red }}>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="e.g. Doe"
                                        isInvalid={!!fieldErrors.lastName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.lastName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Phone Number <span style={{ color: colors.red }}>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        placeholder="+256701234567"
                                        isInvalid={!!fieldErrors.mobile}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.mobile}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Email <span style={{ color: colors.gray[400], fontWeight: 400, fontSize: '0.78rem' }}>(optional)</span></Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="rider@email.com"
                                        isInvalid={!!fieldErrors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldErrors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>National ID <span style={{ color: colors.gray[400], fontWeight: 400, fontSize: '0.78rem' }}>(optional)</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="nationalId"
                                        value={formData.nationalId}
                                        onChange={handleChange}
                                        placeholder="CM12345678ABCD"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Vehicle Type</Form.Label>
                                    <AppSelect block name="vehicleType" value={formData.vehicleType} onChange={handleChange}>
                                        <option value="motorcycle">Motorcycle</option>
                                        <option value="bicycle">Bicycle</option>
                                        <option value="car">Car</option>
                                        <option value="van">Van</option>
                                        <option value="truck">Truck</option>
                                        <option value="walking">Walking / On foot</option>
                                    </AppSelect>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Vehicle Plate Number <span style={{ color: colors.gray[400], fontWeight: 400, fontSize: '0.78rem' }}>(optional)</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vehiclePlateNumber"
                                        value={formData.vehiclePlateNumber}
                                        onChange={handleChange}
                                        placeholder="UAA 123B"
                                    />
                                </Form.Group>
                            </Col>
                            {!editingRider && (
                                <Col md="6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Password{' '}
                                            <span style={{ color: colors.gray[400], fontWeight: 400, fontSize: '0.78rem' }}>
                                                (leave blank to auto-generate)
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Optional"
                                            autoComplete="new-password"
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>Cancel</Button>
                        <Button type="submit" style={{ backgroundColor: colors.blue, borderColor: colors.blue }} disabled={saving}>
                            {saving ? (
                                <><Spinner size="sm" animation="border" className="me-2" />Saving...</>
                            ) : (
                                editingRider ? 'Update Rider' : 'Add Rider'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <RiderCredentialsModal
                show={showCredentialsModal}
                onHide={() => setShowCredentialsModal(false)}
                credentials={createdCredentials}
            />
        </div>
    );
};

export default VendorRiders;
